import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  ModalSubmitButton,
  NewTextInputField,
  showModal,
} from '@edna/components';
import {useOnChange} from '@edna/hooks';

import {Text} from 'src/components/primitives';
import {EChannelType} from 'src/constants';
import {Banner} from 'src/containers/Banner';
import {ELicenseLabelType} from 'src/containers/TariffsCatalog';
import {parseISO} from 'src/utils';

import {useGetAvailableTariffQuery, useGetCatalogTariffLicenseQuery} from '../api';
import {DEFAULT_COEFFICIENT, TAvailableTariff} from '../definitions';
import {CoefficientsForm} from './CoefficientsForm';

const TariffSettingsModal = React.memo<{
  closeModal: TEmptyFunction;
  tariff: TAvailableTariff;
  activeFrom?: string;
}>(({closeModal, tariff, activeFrom}) => {
  const {t} = useTranslation();

  const {data: license} = useGetCatalogTariffLicenseQuery({
    tariffId: tariff.tariffCatalogId,
    licenseId: tariff.licenseDetailId,
  });

  const defaultValues = React.useMemo(() => {
    const baseValues = {
      name: tariff.name,
      license: tariff.data[ELicenseLabelType.LICENSE] ?? DEFAULT_COEFFICIENT,
    };

    if (tariff.channelType === EChannelType.WHATSAPP) {
      return {
        ...baseValues,
        amau: tariff.data[ELicenseLabelType.AMAU] ?? DEFAULT_COEFFICIENT,
        connectionFee: tariff.data[ELicenseLabelType.CONNECTION_FEE] ?? DEFAULT_COEFFICIENT,
      };
    }

    return baseValues;
  }, [tariff]);

  const methods = useForm<TAvailableTariff>({
    mode: 'all',
    defaultValues,
  });

  useOnChange(() => methods.reset(defaultValues), [defaultValues]);

  const hasChanges = activeFrom || tariff.planned;

  return (
    <Modal width="830px">
      <FormProvider {...methods}>
        <ModalHeader>
          {hasChanges
            ? t('Tariffs:editTariffModal.plannedTitle')
            : t(`Tariffs:editTariffModal.settingsTitle`, {
                name: tariff.name,
              })}
        </ModalHeader>
        <ModalContent>
          {hasChanges && (
            <Banner type="INFO" fontSize="S" shadowSize="XS">
              {t('Tariffs:editTariffModal.plannedInfo', {
                date: parseISO(activeFrom ?? tariff.activeFrom, 'DATE_HM'),
              })}
            </Banner>
          )}
          <NewTextInputField name="name" readOnly noMargin />
          <Text topIndent="4" bottomIndent={hasChanges ? '1' : '4'}>
            {t('Tariffs:editTariffModal.coefficients')}
          </Text>
          {hasChanges && (
            <Text bottomIndent="4" secondary size="S">
              {t('Tariffs:editTariffModal.coefficientsHint')}
            </Text>
          )}
          <CoefficientsForm channelType={tariff.channelType} readOnly license={license} />
        </ModalContent>
        <ModalActions>
          <ModalSubmitButton onClick={closeModal}>{t('Common:button.ok')}</ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

TariffSettingsModal.displayName = 'TariffSettingsModal';

const ConnectedTariffSettingsModal = React.memo<{
  closeModal: TEmptyFunction;
  tariffId: number;
  activeFrom: string;
}>(({closeModal, tariffId, activeFrom}) => {
  const {id: tenantId} = useParams<'id'>();

  const {data: tariff} = useGetAvailableTariffQuery({
    id: tariffId,
    tenantId,
  });

  if (!tariff) {
    return null;
  }

  return <TariffSettingsModal closeModal={closeModal} tariff={tariff} activeFrom={activeFrom} />;
});

ConnectedTariffSettingsModal.displayName = 'ConnectedTariffSettingsModal';

export const showTariffSettingsModal = (tariff: TAvailableTariff) =>
  showModal((closeModal) => <TariffSettingsModal closeModal={closeModal} tariff={tariff} />);

export const showConnectedTariffSettingsModal = ({
  tariffId,
  activeFrom,
}: {
  tariffId: number;
  activeFrom: string;
}) =>
  showModal((closeModal) => (
    <ConnectedTariffSettingsModal
      closeModal={closeModal}
      tariffId={tariffId}
      activeFrom={activeFrom}
    />
  ));
